import { Modal, Select, Spin, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { useEffect, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  AttendanceRequest,
  CreateAttendanceRequest,
  GetAttendanceResponse,
  UpdateAttendanceRequest,
} from '@/types/apis/attendance'
import { AttendanceEmployee } from '@/types/apis/attendanceEmployee/GetAttendanceEmployeeByDayResponse'
import { GetAllClientDeviceResponse, GetClientDeviceByDayResponse } from '@/types/apis/clientDevice'
import { GetAllEmployeesResponse } from '@/types/apis/employees'
import { getName } from '@/utils/employee'

import { ButtonCreate, ButtonCustoms, DataCellWrapper, HeaderCellWrapper, TableCustom, Wrappertable } from './styles'

type Props = {
  date?: string | null
  employeeId?: number
  isNotError?: boolean
  getAttendanceResponse: GetAttendanceResponse | undefined
  attendanceLoading: boolean
  updateAttendance: (request: UpdateAttendanceRequest) => Promise<void>
  updateAttendanceLoading: boolean
  getAttendance: (request: AttendanceRequest) => Promise<void>
  createAttendance: (request: CreateAttendanceRequest) => Promise<void>
  createAttendanceLoading: boolean
  responseClientDevice: GetClientDeviceByDayResponse | undefined
  deleteAttendance: (id: number) => Promise<void>
  response: GetAllClientDeviceResponse | undefined
  allEmployeesResponse: GetAllEmployeesResponse | undefined
}

type DataType = {
  key: number
  name: string
  date: string
  employeeId: number
  contactHours: string
  attendance: AttendanceEmployee[]
  workingTime: string
  id: number
}

type CreateEmployee = {
  clientDeviceId: number
  employeeId: number
  checkedAt: string
  type: string
}

const createEmployeeDefault: CreateEmployee = {
  clientDeviceId: 0,
  employeeId: 0,
  checkedAt: '',
  type: '',
}

const AttendanceTable = ({
  date,
  getAttendanceResponse,
  attendanceLoading,
  updateAttendance,
  updateAttendanceLoading,
  getAttendance,
  isNotError,
  employeeId,
  createAttendance,
  createAttendanceLoading,

  deleteAttendance,
  response,
  allEmployeesResponse,
}: Props) => {
  const { t } = useTranslation()
  const [headerList, setHeaderList] = useState<null[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isOpenModalCreate, setIsOpenModalCreate] = useState(false)
  const [isOpenModalAdd, setIsOpenModalAdd] = useState(false)
  const [valueRoom, setValueRoom] = useState({
    id: 0,
    clientDeviceId: 0,
    clientName: '',
    valueRooms: '',
    time: '',
  })

  const [payloadCreate, setPayloadCreate] = useState<CreateEmployee>(createEmployeeDefault)

  const dataSource = getAttendanceResponse ?? []

  const optionSelectClientDevice =
    response?.map((client) => ({
      label: client.name,
      value: client.id,
    })) ?? []

  useEffect(() => {
    if (getAttendanceResponse) {
      const maxLength = Math.max(...getAttendanceResponse.map((arr) => arr.attendances.length))
      if (maxLength > 0) {
        const newArray: null[] = Array(maxLength).fill('')
        setHeaderList(newArray)
      }
    }
  }, [getAttendanceResponse])

  const handleUpdateEmployee = (
    id: number,
    clientName: string,
    valueRoom: string,
    time: string,
    clientDeviceId: number
  ) => {
    setIsOpen(true)
    setValueRoom({ id, clientName, valueRooms: valueRoom, time, clientDeviceId })
  }

  const handleCancel = () => {
    setValueRoom({
      id: 0,
      clientName: '',
      valueRooms: '',
      time: '',
      clientDeviceId: 0,
    })
    setIsOpen(false)
  }

  const handleChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValueRoom((pre) => ({ ...pre, time: e.target.value }))
  }

  const handleChangeHistory = (value: string) => {
    setValueRoom((pre) => ({ ...pre, valueRooms: value }))
  }

  const handleUpdate = async () => {
    if (date) {
      const [hour, minute] = valueRoom.time.split(':')
      const dateCurrent = new Date(date)
      dateCurrent.setHours(parseInt(hour, 10))
      dateCurrent.setMinutes(parseInt(minute, 10))
      valueRoom.valueRooms === 'IN' ? dateCurrent.setSeconds(0) : dateCurrent.setSeconds(59)
      await updateAttendance({
        id: valueRoom.id,
        time: dateCurrent.toISOString(),
        type: valueRoom.valueRooms,
        clientDeviceId: valueRoom.clientDeviceId,
      })
      await getAttendance({
        date,
        employeeId: employeeId !== -1 ? employeeId : undefined,
        isNotError,
      })
      setIsOpen(false)
    }
  }

  const handleDelete = async () => {
    if (date && valueRoom) {
      await deleteAttendance(valueRoom.id)
      await getAttendance({
        date,
        employeeId: employeeId !== -1 ? employeeId : undefined,
        isNotError,
      })
      setIsOpen(false)
    }
  }

  const defaultOption = {
    label: t('components.pages.attendanceEmployee.listReport.filter.defaultValue'),
    value: 0,
  }

  const optionCustom = optionSelectClientDevice && [defaultOption, ...optionSelectClientDevice]

  const optionEmployee = allEmployeesResponse?.map((item) => {
    return {
      label: getName(item),
      value: item.id,
    }
  })

  const optionCustomEmployee = optionEmployee && [defaultOption, ...optionEmployee]

  const handleAddEmployee = () => {
    setPayloadCreate(createEmployeeDefault)
    setIsOpenModalAdd(true)
  }

  const handleCancelModalAddEmployee = () => {
    setPayloadCreate(createEmployeeDefault)
    setIsOpenModalAdd(false)
  }

  const handleSelectEmployee = (value: number) => {
    setPayloadCreate((pre) => ({ ...pre, employeeId: value }))
  }

  const handleCreateAttendance = (value: number) => {
    setIsOpenModalCreate(true)
    setPayloadCreate((pre) => ({ ...pre, employeeId: value }))
  }

  const handleSelectClient = (value: number) => {
    setPayloadCreate((pre) => ({ ...pre, clientDeviceId: value }))
  }

  const handleChangeCreateType = (value: string) => {
    setPayloadCreate((pre) => ({ ...pre, type: value }))
  }

  const handleChangeCreateTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPayloadCreate((pre) => ({ ...pre, checkedAt: e.target.value }))
  }

  const handleAdd = async () => {
    if (date) {
      const [hour, minute] = payloadCreate.checkedAt.split(':')
      const dateCurrent = new Date(date)
      dateCurrent.setHours(parseInt(hour, 10))
      dateCurrent.setMinutes(parseInt(minute, 10))
      payloadCreate.type === 'IN' ? dateCurrent.setSeconds(0) : dateCurrent.setSeconds(59)
      await createAttendance({
        employeeId: payloadCreate.employeeId,
        clientDeviceId: payloadCreate.clientDeviceId,
        checkedAt: dateCurrent.toISOString(),
        type: payloadCreate.type,
      })
      await getAttendance({
        date,
        employeeId: employeeId !== -1 ? employeeId : undefined,
        isNotError,
      })
      setPayloadCreate(createEmployeeDefault)
      setIsOpenModalAdd(false)
    }
  }

  const handleCancelModalCreate = () => {
    setPayloadCreate(createEmployeeDefault)
    setIsOpenModalCreate(false)
  }

  const handleCreate = async () => {
    if (date) {
      const [hour, minute] = payloadCreate.checkedAt.split(':')
      const dateCurrent = new Date(date)
      dateCurrent.setHours(parseInt(hour, 10))
      dateCurrent.setMinutes(parseInt(minute, 10))
      payloadCreate.type === 'IN' ? dateCurrent.setSeconds(0) : dateCurrent.setSeconds(59)
      await createAttendance({
        employeeId: payloadCreate.employeeId,
        clientDeviceId: payloadCreate.clientDeviceId,
        checkedAt: dateCurrent.toISOString(),
        type: payloadCreate.type,
      })
      await getAttendance({
        date,
        employeeId: employeeId !== -1 ? employeeId : undefined,
        isNotError,
      })
      setPayloadCreate(createEmployeeDefault)
      setIsOpenModalCreate(false)
    }
  }

  const columns: ColumnsType<DataType> = [
    {
      title: t('components.pages.attendanceEmployee.listReport.table.date'),
      width: 110,
      dataIndex: 'date',
      key: 'date',
      fixed: 'left',
      align: 'center',
      render: (text) => <div style={{ textAlign: 'right', paddingRight: 5 }}>{text}</div>,
    },
    {
      title: t('components.pages.attendanceEmployee.listReport.table.id'),
      width: 80,
      dataIndex: 'employeeId',
      key: 'employeeId',
      fixed: 'left',
      align: 'center',
      render: (text) => <div style={{ textAlign: 'right', paddingRight: 5 }}>{text}</div>,
    },
    {
      title: t('components.pages.attendanceEmployee.listReport.table.fullName'),
      width: 110,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      align: 'center',
      render: (text) => <div style={{ textAlign: 'left', paddingLeft: 5 }}>{text}</div>,
    },
    {
      title: t('components.pages.attendance.table.contactHours'),
      width: 100,
      dataIndex: 'contactHours',
      key: 'contactHours',
      fixed: 'left',
      align: 'center',
      render: (text) => <div style={{ textAlign: 'right', paddingRight: 5 }}>{text}</div>,
    },
    {
      title: t('components.pages.attendance.table.workingTime'),
      width: 100,
      dataIndex: 'workingTime',
      key: 'workingTime',
      fixed: 'left',
      align: 'center',
      render: (text) => <div style={{ textAlign: 'right', paddingRight: 5 }}>{text}</div>,
    },
    {
      title: t('components.pages.attendance.table.addTabs'),
      width: 90,
      fixed: 'left',
      align: 'center',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_, record: any) => (
        <div
          className="item_active"
          style={{ cursor: 'pointer' }}
          onClick={() => handleCreateAttendance(record.employeeId)}
        >
          {'+'}
        </div>
      ),
    },
  ]

  headerList.forEach((_, index) => {
    const dataColumn: ColumnsType<DataType> = [
      {
        title: t('components.pages.attendance.table.process'),
        width: 120,
        className: index % 2 === 0 ? 'active' : '',
        dataIndex: `device-${index}`,
        key: index + 1,
        align: 'center',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_, record: any) => {
          const id = record[`id-${index}`]
          const clientName = record[`device-${index}`]
          const type = record[`type-${index}`]
          const time = record[`time-${index}`]
          const clientId = record[`client-device-${index}`]
          return (
            <div
              className={!record[`error-${index}`] ? 'success' : 'error'}
              style={{ cursor: 'pointer' }}
              onClick={() => handleUpdateEmployee(id, clientName, type, time, clientId)}
            >
              {record[`device-${index}`]}
            </div>
          )
        },
      },
      {
        title: t('components.pages.attendance.table.inOut'),
        width: 100,
        dataIndex: `type-${index}`,
        className: index % 2 === 0 ? 'active' : '',
        key: index + 1,
        align: 'center',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_, record: any) => {
          const id = record[`id-${index}`]
          const clientName = record[`device-${index}`]
          const type = record[`type-${index}`]
          const time = record[`time-${index}`]
          const clientId = record[`client-device-${index}`]
          return (
            <div
              className={!record[`error-${index}`] ? 'success' : 'error'}
              style={{ cursor: 'pointer' }}
              onClick={() => handleUpdateEmployee(id, clientName, type, time, clientId)}
            >
              {record[`type-${index}`]}
            </div>
          )
        },
      },
      {
        title: t('components.pages.attendance.table.time'),
        width: 80,
        dataIndex: `time-${index}`,
        className: index % 2 === 0 ? 'active' : '',
        key: index + 1,
        align: 'center',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_, record: any) => {
          const id = record[`id-${index}`]
          const clientName = record[`device-${index}`]
          const type = record[`type-${index}`]
          const time = record[`time-${index}`]
          const clientId = record[`client-device-${index}`]
          return (
            <div
              className={!record[`error-${index}`] ? 'success' : 'error'}
              style={{ cursor: 'pointer' }}
              onClick={() => handleUpdateEmployee(id, clientName, type, time, clientId)}
            >
              {record[`time-${index}`]}
            </div>
          )
        },
      },
    ]
    columns.push(...dataColumn)
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataTable: any[] = []
  dataSource.forEach((employee, index) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const commonData: any = {
      key: index,
      date: moment(date).format('YYYY/MM/DD'),
      employeeId: employee.id,
      name: getName(employee),
      contactHours: employee.contractHours.toFixed(1),
      workingTime: employee.workingTime.toFixed(1),
      attendance: employee.attendances,
    }

    employee.attendances?.forEach((item, idx) => {
      commonData[`id-${idx}`] = item.id
      commonData[`device-${idx}`] = item.clientName
      commonData[`type-${idx}`] = item.type
      commonData[`time-${idx}`] = moment(item.checkedAt).format('HH:mm')
      commonData[`error-${idx}`] = item.isError
      commonData[`client-device-${idx}`] = item.clientDeviceId
    })
    dataTable.push(commonData)
  })

  return (
    <Wrappertable className="table-error">
      <Spin spinning={attendanceLoading || updateAttendanceLoading || createAttendanceLoading}>
        {dataTable && (
          <>
            <ButtonCreate disabled={!date} onClick={handleAddEmployee}>
              {t('components.pages.attendance.table.addEmployee')}
            </ButtonCreate>
            <Table
              style={{ border: '1px solid', borderTop: 'none', borderRight: 'none' }}
              columns={columns}
              dataSource={dataTable}
              scroll={{ x: 100, y: 600 }}
              pagination={false}
            />
          </>
        )}
        <Modal
          onOk={handleUpdate}
          title={t('components.pages.attendance.table.update')}
          visible={isOpen}
          onCancel={handleCancel}
          okText={t('components.pages.attendance.table.save')}
        >
          <div>
            <ButtonCustoms danger onClick={handleDelete}>
              {t('components.pages.attendance.table.delete')}
            </ButtonCustoms>
            <TableCustom>
              <tr>
                <HeaderCellWrapper>{t('components.pages.attendance.table.process')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.inOut')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.time')}</HeaderCellWrapper>
              </tr>
              <tbody>
                <tr>
                  <DataCellWrapper>
                    <Select
                      style={{ width: 140 }}
                      options={optionCustom}
                      onChange={(value) => setValueRoom((pre) => ({ ...pre, clientDeviceId: value }))}
                      value={valueRoom.clientDeviceId}
                    />
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <Select onChange={(value) => handleChangeHistory(value)} value={valueRoom.valueRooms}>
                      <Select.Option value={'IN'}>{t('IN')}</Select.Option>
                      <Select.Option value={'OUT'}>{'OUT'}</Select.Option>
                    </Select>
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <input onChange={(e) => handleChangeTime(e)} type="time" value={valueRoom.time} />
                  </DataCellWrapper>
                </tr>
              </tbody>
            </TableCustom>
          </div>
        </Modal>
        <Modal
          onOk={handleCreate}
          title={t('components.pages.attendance.table.create')}
          visible={isOpenModalCreate}
          onCancel={handleCancelModalCreate}
          okText={t('components.pages.attendance.table.save')}
        >
          <div>
            <TableCustom>
              <tr>
                <HeaderCellWrapper>{t('components.pages.attendance.table.process')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.inOut')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.time')}</HeaderCellWrapper>
              </tr>
              <tbody>
                <tr>
                  <DataCellWrapper>
                    <Select
                      showSearch
                      style={{ width: 140 }}
                      onChange={(value) => handleSelectClient(value)}
                      value={payloadCreate.clientDeviceId}
                      options={optionCustom}
                      filterOption={(input, option) => (option?.label ?? '').includes(input)}
                    />
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <Select
                      value={payloadCreate.type}
                      style={{ width: 150 }}
                      onChange={(value) => handleChangeCreateType(value)}
                    >
                      <Select.Option value={'IN'}>{t('IN')}</Select.Option>
                      <Select.Option value={'OUT'}>{'OUT'}</Select.Option>
                    </Select>
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <input onChange={(e) => handleChangeCreateTime(e)} type="time" value={payloadCreate.checkedAt} />
                  </DataCellWrapper>
                </tr>
              </tbody>
            </TableCustom>
          </div>
        </Modal>
        <Modal
          onOk={handleAdd}
          title={t('components.pages.attendance.table.addEmployee')}
          visible={isOpenModalAdd}
          onCancel={handleCancelModalAddEmployee}
          okText={t('components.pages.attendance.table.save')}
          width={700}
        >
          <div>
            <TableCustom>
              <tr>
                <HeaderCellWrapper>{t('components.pages.attendance.table.name')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.process')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.inOut')}</HeaderCellWrapper>
                <HeaderCellWrapper>{t('components.pages.attendance.table.time')}</HeaderCellWrapper>
              </tr>
              <tbody>
                <tr>
                  <DataCellWrapper>
                    <Select
                      showSearch
                      style={{ width: 140 }}
                      onChange={(value) => handleSelectEmployee(value)}
                      value={payloadCreate.employeeId}
                      options={optionCustomEmployee}
                      filterOption={(input, option) => (option?.label ?? '').includes(input)}
                    />
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <Select
                      showSearch
                      style={{ width: 140 }}
                      onChange={(value) => handleSelectClient(value)}
                      value={payloadCreate.clientDeviceId}
                      options={optionCustom}
                      filterOption={(input, option) => (option?.label ?? '').includes(input)}
                    />
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <Select
                      value={payloadCreate.type}
                      style={{ width: 150 }}
                      onChange={(value) => handleChangeCreateType(value)}
                    >
                      <Select.Option value={'IN'}>{t('IN')}</Select.Option>
                      <Select.Option value={'OUT'}>{'OUT'}</Select.Option>
                    </Select>
                  </DataCellWrapper>
                  <DataCellWrapper>
                    <input onChange={(e) => handleChangeCreateTime(e)} type="time" value={payloadCreate.checkedAt} />
                  </DataCellWrapper>
                </tr>
              </tbody>
            </TableCustom>
          </div>
        </Modal>
      </Spin>
    </Wrappertable>
  )
}
AttendanceTable.displayName = 'AttendanceTable'
export default AttendanceTable
